<script setup lang="ts">
import ActivityEditor from './ActivityEditor.vue'
import { TextField } from '@haskoning/vue-component-library'
import type { MaterialDto } from '@/stores/buildingBlock'
import type { MaterialEditorProps } from '@/components/building-block-form/type/type'
import { useMaterialEditor } from '@/components/building-block-form/composable/useMaterialEditor'
import { handleWheelOnNumericField } from '@/utils/handleWheelOnNumericField'

const props = defineProps<MaterialEditorProps>()

const {
  materialStore,
  activityItems,
  infoMenu,
  currentProduct,
  hasProduct,
  visibleComparisonFields,
  isGroupColumn,
  isDifferent,
  materialImpactCalculated,
  onName,
  onLifetimeTyping,
  onConversionFactorTyping,
  deleteMaterial,
  createActivity,
  isFirstActivityForStage,
  sameStageCount,
  onStageChanged,
  onModeChanged,
  getComparisonDisplayValue
} = useMaterialEditor(props)
</script>

<template>
  <div class="material-editor px-5 mb-2">
    <div class="d-flex align-center my-1">
      <span class="text-subtitle-2"
        >{{ props.rowLabel ?? (props.isEquipmentBlock ? 'Equipment' : 'Material') }} -
        {{ props.material.name }} - {{ props.material.unit }}</span
      >
      <span class="ml-4 text-body-2 text-medium-emphasis"
        >{{
          props.rowImpactLabel ??
          (props.isEquipmentBlock ? 'Equipment Impact:' : 'Material impact:')
        }}{{ materialImpactCalculated }}</span
      >
      <v-menu max-width="2000" v-model="infoMenu" location="bottom" :close-on-content-click="false">
        <template #activator="{ props }">
          <v-btn
            class="ma-2"
            border="md primary opacity-75"
            v-bind="props"
            icon="mdi-information-symbol"
            size="medium"
            variant="outlined"
          />
        </template>

        <v-card class="border-md" style="padding: 12px">
          <v-card-title class="text-subtitle-2">{{ props.material.name }}</v-card-title>
          <v-table density="compact">
            <thead>
              <tr>
                <th class="group-column">Source</th>
                <th
                  v-for="field in visibleComparisonFields"
                  :key="field.key"
                  :class="{ 'group-column': isGroupColumn(field.key) }"
                >
                  {{ field.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="font-weight-medium group-column">Default</td>
                <td
                  v-for="field in visibleComparisonFields"
                  :key="field.key"
                  :class="{
                    'group-column': isGroupColumn(field.key),
                    'highlighted-diff': isDifferent(field.key as keyof MaterialDto)
                  }"
                >
                  {{
                    getComparisonDisplayValue(currentProduct, field.key) != null
                      ? getComparisonDisplayValue(currentProduct, field.key)!.toFixed(5)
                      : '-'
                  }}
                </td>
              </tr>
              <tr>
                <td class="font-weight-medium group-column">New</td>
                <td
                  v-for="field in visibleComparisonFields"
                  :key="field.key"
                  :class="{
                    'group-column': isGroupColumn(field.key),
                    'highlighted-diff': isDifferent(field.key as keyof MaterialDto)
                  }"
                >
                  {{
                    getComparisonDisplayValue(props.material, field.key) != null
                      ? getComparisonDisplayValue(props.material, field.key)!.toFixed(5)
                      : '-'
                  }}
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-card>
      </v-menu>

      <v-spacer />
      <v-btn
        icon="mdi-delete"
        variant="text"
        size="small"
        :disabled="!props.canDelete"
        @click="deleteMaterial"
      />
    </div>

    <v-row>
      <v-col cols="12" :md="props.isEquipmentBlock ? 8 : 6" class="ma-0">
        <v-text-field
          :model-value="props.material.name"
          :label="props.nameFieldLabel ?? 'Material name'"
          color="primary"
          density="comfortable"
          :readonly="props.readonly || hasProduct"
          hide-details
          @input="onName(($event.target as HTMLInputElement).value)"
        />
      </v-col>
      <v-col v-if="!props.isEquipmentBlock" cols="12" md="2" class="ma-0">
        <TextField
          v-if="materialStore.fields?.lifetime"
          :field="materialStore.fields?.lifetime"
          :model-value="props.material"
          density="comfortable"
          :no-help-text="true"
          hide-details
          :readonly="props.readonly"
          @wheel="handleWheelOnNumericField"
          @input="onLifetimeTyping"
        />
      </v-col>
      <v-col cols="12" md="2" class="ma-0">
        <TextField
          v-if="materialStore.fields?.conversion_factor"
          :field="materialStore.fields?.conversion_factor"
          :model-value="props.material"
          density="comfortable"
          :no-help-text="true"
          hide-details
          :readonly="props.readonly || props.isEquipmentBlock"
          @wheel="handleWheelOnNumericField"
          @input="onConversionFactorTyping"
        />
      </v-col>
      <v-col cols="12" md="2" class="ma-0">
        <v-text-field
          :model-value="props.material.impact_unit"
          label="Impact Unit"
          color="primary"
          variant="outlined"
          density="comfortable"
          hide-details
          readonly
        />
      </v-col>
    </v-row>
    <h6 class="my-3">{{ currentProduct?.description }}</h6>

    <div>
      <div v-for="activity in activityItems" :key="activity.id" class="mb-4">
        <ActivityEditor
          v-if="activity.material === props.material.id"
          :activity="activity"
          :material="props.material"
          :is-equipment-block="props.isEquipmentBlock"
          :readonly="props.readonly"
          :product="currentProduct"
          :used-stages="
            activityItems
              .filter(
                (otherActivity) =>
                  otherActivity.material === props.material.id && otherActivity.id !== activity.id
              )
              .map((otherActivity) => otherActivity.stage)
          "
          :is-first-for-stage="isFirstActivityForStage(activity.id, activity.stage)"
          :same-stage-count="sameStageCount(activity.stage)"
          :on-stage-changed="onStageChanged"
          :on-mode-changed="onModeChanged"
        />
      </div>
      <v-btn
        class="mb-4"
        color="primary"
        prepend-icon="mdi-plus"
        :disabled="props.readonly"
        @click="createActivity()"
      >
        Add activity
      </v-btn>
    </div>
  </div>
</template>

<style scoped>
.material-editor {
  border: 1px solid #c0c0c0;
  border-radius: 10px;
}

.group-column {
  background-color: rgb(221, 225, 226);
}

.highlighted-diff {
  background-color: #fff9c4;
  font-weight: 500;
}
</style>
